import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  CircleNotch,
  CheckCircle,
  LockSimple,
  UploadSimple,
} from '@phosphor-icons/react';
import { integrationsApi, type CrmAvailability, type CrmExportResult } from '../../../lib/api';
import { usePermission } from '../../../hooks/usePermission';
import { useSettingsStore } from '../../../stores/settingsStore';
import { useScrollLock } from '../../../lib/useScrollLock';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import zohoIcon from '@/assets/providers/zoho-crm.png';
import hubspotIcon from '@/assets/providers/hubspot.png';
import salesforceIcon from '@/assets/providers/salesforce.webp';
import pipedriveIcon from '@/assets/providers/pipedrive.png';

// The four CRMs whose backend adapters expose a contact upsert. The ids match
// the booleans returned by GET /integrations/crm/availability.
type CrmId = 'hubspot' | 'salesforce' | 'pipedrive' | 'zoho';

interface CrmDef {
  id: CrmId;
  name: string;
  tagline: string;
  img: string;
  // `fill` = full-bleed colored logo tile (object-cover); otherwise white-boxed wordmark.
  fill?: boolean;
}

const CRMS: CrmDef[] = [
  { id: 'hubspot', name: 'HubSpot', tagline: 'Upsert contacts by email', img: hubspotIcon, fill: true },
  { id: 'salesforce', name: 'Salesforce', tagline: 'Upsert contacts by email', img: salesforceIcon },
  { id: 'pipedrive', name: 'Pipedrive', tagline: 'Create people records', img: pipedriveIcon, fill: true },
  { id: 'zoho', name: 'Zoho CRM', tagline: 'Upsert contacts & leads', img: zohoIcon },
];

interface CrmExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Explicit contact selection to export (mutually exclusive with listId/tag). */
  contactIds?: number[];
  /** Whole-list export — the backend resolves current members. */
  listId?: number;
  /** Whole-tag export — the backend resolves every contact carrying the tag. */
  tag?: string;
  /** How many contacts the source holds — for the header copy. */
  contactCount?: number;
  /** Human label for the source (e.g. a list name) — for the header copy. */
  sourceName?: string;
  /** Called after a successful export (e.g. to clear a selection). */
  onExported?: () => void;
}

export function CrmExportModal({
  isOpen,
  onClose,
  contactIds,
  listId,
  tag,
  contactCount,
  sourceName,
  onExported,
}: CrmExportModalProps) {
  useScrollLock(isOpen);

  // Settings config carries per-integration connection-test results
  // (integration_test_status), which we use to grey out a CRM whose last test failed.
  const { config, fetchConfig } = useSettingsStore();

  // A Representative's scope on contacts.bulk_export is "gated": clicking a CRM
  // files a request instead of pushing. Nothing else about the modal changes —
  // the backend is what actually decides, this only makes the copy honest.
  const { scope: exportScope } = usePermission('contacts', 'bulk_export');
  const requestsApproval = exportScope === 'gated';

  const [status, setStatus] = useState<CrmAvailability | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusError, setStatusError] = useState(false);
  // Which CRM is mid-export (also disables the whole grid while running).
  const [exporting, setExporting] = useState<CrmId | null>(null);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    setStatusError(false);
    try {
      const res = await integrationsApi.crmAvailability();
      setStatus(res.data);
    } catch {
      setStatusError(true);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadStatus();
      if (!config) fetchConfig();
    }
     
  }, [isOpen]);

  const handleExport = async (crm: CrmDef) => {
    if (exporting) return;
    setExporting(crm.id);
    try {
      // Exactly one source is sent: tag > list > explicit ids.
      const res = await integrationsApi.crmExportContacts({
        provider: crm.id,
        tag: tag ?? undefined,
        list_id: tag == null ? (listId ?? undefined) : undefined,
        contact_ids: tag == null && listId == null ? contactIds : undefined,
      });
      const r: CrmExportResult = res.data;
      if (r.status === 'pending_approval') {
        toast.success(`Sent to your manager for approval`, {
          description: `${r.total} contact${r.total === 1 ? '' : 's'} will go to ${crm.name} once approved.`,
        });
        onExported?.();
        setExporting(null);
        setStatus(null);
        setStatusError(false);
        onClose();
        return;
      }
      const parts = [`Exported ${r.exported} contact${r.exported === 1 ? '' : 's'} to ${crm.name}`];
      if (r.failed > 0) parts.push(`${r.failed} failed`);
      if (r.skipped > 0) parts.push(`${r.skipped} skipped (no email)`);
      if (r.failed > 0) {
        toast.warning(parts.join(', '), {
          description: r.errors[0]?.error
            ? `First error: ${r.errors[0].error}`
            : undefined,
          duration: 7000,
        });
      } else {
        toast.success(parts.join(', '));
      }
      onExported?.();
      setExporting(null);
      setStatus(null);
      setStatusError(false);
      onClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail ||
          (requestsApproval ? `Failed to request an export to ${crm.name}` : `Failed to export to ${crm.name}`),
      );
    } finally {
      setExporting(null);
    }
  };

  const handleClose = () => {
    if (exporting) return; // don't close mid-export
    setStatus(null);
    setStatusError(false);
    onClose();
  };

  // Escape dismissal. Routed through handleClose (never onClose directly) so the
  // mid-export guard above still applies — `exporting` is in the deps so the
  // listener always closes over the current in-flight state.
  // Must stay above the early return — hooks can't run conditionally.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
     
  }, [isOpen, onClose, exporting]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const headerCount =
    typeof contactCount === 'number'
      ? contactCount
      : contactIds?.length ?? 0;

  return createPortal(
    (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="crm-export-title"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl flex flex-col rounded-xl overflow-hidden bg-card border border-border/60 shadow-xl dark:bg-[#191a1b] dark:border-white/[0.08]"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/50 dark:border-white/[0.06] shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] flex items-center justify-center shrink-0">
                  <UploadSimple className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                </div>
                <div className="min-w-0">
                  <h2
                    id="crm-export-title"
                    className="text-foreground dark:text-[#f7f8f8]"
                    style={{ fontSize: '16px', fontWeight: 590, letterSpacing: '-0.18px', lineHeight: 1.3 }}
                  >
                    {requestsApproval ? 'Request CRM export' : 'Export to CRM'}
                  </h2>
                  <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                    {requestsApproval ? 'Send' : 'Upload'} {headerCount > 0 ? `${headerCount} ` : ''}contact{headerCount === 1 ? '' : 's'}
                    {sourceName ? ` from "${sourceName}"` : ''} to a connected CRM.
                    {requestsApproval ? ' A manager approves before anything is uploaded.' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={!!exporting}
                aria-label="Close"
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              {loadingStatus ? (
                <div className="flex flex-col items-center justify-center py-14 gap-3">
                  <CircleNotch className="h-6 w-6 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc]" />
                  <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px' }}>
                    Checking your CRM connections…
                  </p>
                </div>
              ) : statusError ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                  <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '14px', fontWeight: 590 }}>
                    Couldn't load your integrations
                  </p>
                  <button
                    onClick={loadStatus}
                    className="text-[13px] font-medium text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {CRMS.map((crm) => {
                      const hasCreds = Boolean(status?.[crm.id]);
                      // A saved-but-failed connection test blocks export just like
                      // missing credentials would — you can't push to a broken link.
                      const testFailed = config?.integration_test_status?.[crm.id]?.status === 'failed';
                      const connected = hasCreds && !testFailed;
                      const isExporting = exporting === crm.id;
                      const disabled = !connected || (!!exporting && !isExporting);
                      const disabledLabel = testFailed ? 'Connection failed' : 'Not connected';
                      const disabledTitle = testFailed
                        ? `Last connection test to ${crm.name} failed — reconnect in Settings → Integrations`
                        : `Connect ${crm.name} in Settings → Integrations to export`;
                      return (
                        <button
                          key={crm.id}
                          type="button"
                          disabled={disabled}
                          onClick={() => handleExport(crm)}
                          title={connected ? undefined : disabledTitle}
                          className={cn(
                            'group relative flex items-center gap-3 rounded-xl border p-4 text-left transition-all duration-150',
                            connected
                              ? 'border-border/60 bg-card hover:border-[#5e6ad2]/60 hover:bg-[#5e6ad2]/[0.04] dark:border-white/[0.08] dark:bg-white/[0.02] dark:hover:border-[#7170ff]/50 dark:hover:bg-[#7170ff]/[0.06]'
                              : 'border-border/40 bg-muted/30 opacity-55 cursor-not-allowed dark:border-white/[0.05] dark:bg-white/[0.02]',
                            isExporting && 'border-[#5e6ad2]/60 dark:border-[#7170ff]/50',
                          )}
                        >
                          {/* Icon tile */}
                          <div
                            className={cn(
                              'h-10 w-10 rounded-lg flex items-center justify-center shrink-0 overflow-hidden ring-1 ring-border/50 dark:ring-white/[0.08]',
                              !crm.fill && 'bg-white',
                            )}
                          >
                            {crm.fill ? (
                              <img src={crm.img} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <img src={crm.img} alt="" className="h-6 w-6 object-contain" />
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p
                                className="text-foreground dark:text-[#f7f8f8] truncate"
                                style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                              >
                                {crm.name}
                              </p>
                              {connected && (
                                <CheckCircle className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400 shrink-0" weight="fill" />
                              )}
                            </div>
                            <p
                              className="text-muted-foreground dark:text-[#8a8f98] truncate"
                              style={{ fontSize: '11.5px', letterSpacing: '-0.1px' }}
                            >
                              {connected ? crm.tagline : disabledLabel}
                            </p>
                          </div>

                          {/* Trailing state */}
                          {isExporting ? (
                            <CircleNotch className="h-4 w-4 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc] shrink-0" />
                          ) : !connected ? (
                            <LockSimple className="h-4 w-4 text-muted-foreground/70 dark:text-[#62666d] shrink-0" />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>

                  <p className="mt-4 text-muted-foreground/80 dark:text-[#62666d]" style={{ fontSize: '11.5px', letterSpacing: '-0.05px' }}>
                    {requestsApproval
                      ? "Greyed-out CRMs aren't connected on your account. Your request goes to a manager — nothing is uploaded until they approve it. Contacts without an email address are skipped."
                      : "Greyed-out CRMs aren't connected or failed their last connection test — check them in Settings → Integrations. Contacts without an email address are skipped."}
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    ),
    document.body,
  );
}
